import React, { useEffect, useState } from "react";
import { useQwanyx } from "qwanyx";
import { newId } from "../../qwanyx-core/newId";

// Global shortcut registry that persists across renders and component visibility
const shortcutRegistry = {};

// Initialize the global keydown listener (runs only once)
if (typeof window !== 'undefined' && !window.__shortcutListenerInitialized) {
  window.addEventListener('keydown', (event) => {
    // Build shortcut string (e.g., "Alt 1")
    let shortcutString = '';
    
    if (event.altKey) shortcutString += 'Alt ';
    if (event.ctrlKey) shortcutString += 'Ctrl ';
    if (event.shiftKey) shortcutString += 'Shift ';
    
    shortcutString += event.key;
    
    // Check if this shortcut is registered
    if (shortcutRegistry[shortcutString]) {
      const { groupName, updateCardByName, getCardByName, debug } = shortcutRegistry[shortcutString];
      
      if (debug) {
        console.log(`Shortcut triggered: "${shortcutString}" for group ${groupName}`);
      }
      
      // Get current visibility state and toggle it
      const groupData = shortcutRegistry[shortcutString];
      const currentState = getCardByName ? getCardByName(groupName) : null;
      const currentVisibility = currentState?.isVisible !== false;
      const newVisibility = !currentVisibility;
      
      // Update visibility in registry
      updateCardByName(groupName, { isVisible: newVisibility });
      
      if (debug) {
        console.log(`Shortcut handler: Toggled group ${groupName} visibility to ${newVisibility} via global shortcut`);
      }
      
      // Prevent default browser behavior
      event.preventDefault();
    }
  });
  
  window.__shortcutListenerInitialized = true;
}

/**
 * GroupContainer - A container component that adds visibility control 
 * to any children components
 * 
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - The children to show/hide
 * @param {boolean} props.useGlobalContext - Whether to use the global context
 * @param {string} props.groupName - Name/key for this group instance (required for registry)
 * @param {boolean} props.debug - Whether to show debug information
 * @param {boolean} props.isVisible - Direct visibility control prop (optional)
 * @param {string} props.shortcut - Keyboard shortcut (e.g., "Alt 1") to toggle visibility
 * @param {string} props.className - Additional CSS classes to apply to the wrapper div
 * @param {Object} props.style - Additional inline styles to apply to the wrapper div
 * @returns {JSX.Element|null} - Children components wrapped in a div or null if hidden
 */
export const GroupContainer = ({ 
  children,
  useGlobalContext = false,
  groupName: externalGroupName, 
  debug = false,
  isVisible = true, 
  shortcut,
  className = "",
  style = {},
  ...otherProps
}) => {
  // Generate a name if not provided
  const groupName = externalGroupName || `group_${newId()}`;
  
  // Get state and functions from QwanyxProvider
  const { 
    // Card visibility
    isCardVisible: globalVisibility,
    
    // Registry functions - use the card registry functions
    registerCard,
    getCardByName,
    updateCardByName,
  } = useQwanyx();
  
  // Local state to track this group's properties 
  const [localGroupState, setLocalGroupState] = useState({
    isVisible,
    groupName
  });
  
  // Register the group with the provider when it mounts or when key props change
  useEffect(() => {
    if (!groupName) {
      if (debug) {
        console.log("GroupContainer: No groupName provided, cannot register with registry");
      }
      return;
    }
    
    // Prepare group data for registration
    const groupData = {
      isVisible,
      groupName,
      registeredAt: Date.now()
    };
    
    // Register with the global registry
    registerCard(groupName, groupData);
    
    // Update local state
    setLocalGroupState(groupData);
    
    if (debug) {
      console.log(`GroupContainer: Registered group ${groupName} with registry`);
    }
  }, [groupName, isVisible, registerCard, debug]);
  
  // Listen for updates to this group in the registry
  useEffect(() => {
    if (!groupName) return;
    
    // Function to check for updates from registry
    const checkForUpdates = () => {
      // Get current state from registry
      const registryGroupData = getCardByName(groupName);
      
      if (registryGroupData) {
        // Update local state when registry changes
        setLocalGroupState(prev => {
          // Only update if there's an actual change
          if (prev.isVisible !== registryGroupData.isVisible) {
            if (debug) {
              console.log(`GroupContainer: Updated from registry for ${groupName}`, registryGroupData);
            }
            return registryGroupData;
          }
          return prev;
        });
      }
    };
    
    // Check immediately on mount or deps change
    checkForUpdates();
    
    // Set up a polling interval to check for registry updates
    // This helps catch updates made by other components or through global shortcuts
    const intervalId = setInterval(checkForUpdates, 200);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [groupName, getCardByName, debug]);
  
  // LOCAL toggle function
  const toggleVisibility = () => {
    // Get latest group data to ensure we have current state
    const groupData = getCardByName(groupName);
    const newVisibility = !(groupData?.isVisible);
    
    // Update in registry
    updateCardByName(groupName, { isVisible: newVisibility });
    
    if (debug) {
      console.log(`GroupContainer: Toggled visibility for ${groupName} to ${newVisibility}`);
    }
  };
  
  // Key effect: Register shortcut when component mounts
  useEffect(() => {
    if (!shortcut || !groupName) return;
    
    // Register in the global shortcut registry
    shortcutRegistry[shortcut] = {
      groupName,
      updateCardByName, // Store the function reference for the global handler
      getCardByName,    // Add getCardByName to check current state
      debug
    };
    
    if (debug) {
      console.log(`GroupContainer: Registered shortcut "${shortcut}" for group ${groupName}`);
    }
    
    // Set up a local keydown handler for when the group is visible
    const handleKeyDown = (event) => {
      let keyCombo = '';
      
      if (event.altKey) keyCombo += 'Alt ';
      if (event.ctrlKey) keyCombo += 'Ctrl ';
      if (event.shiftKey) keyCombo += 'Shift ';
      
      keyCombo += event.key;
      
      if (keyCombo === shortcut) {
        if (debug) {
          console.log(`GroupContainer: Local shortcut handler triggered for ${groupName}`);
        }
        
        // Call the toggle function
        toggleVisibility();
        
        // Prevent default browser behavior
        event.preventDefault();
      }
    };
    
    // Add local handler for when component is rendered
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up both global and local handlers when unmounted
    return () => {
      delete shortcutRegistry[shortcut];
      window.removeEventListener('keydown', handleKeyDown);
      
      if (debug) {
        console.log(`GroupContainer: Cleaned up shortcut handlers for ${groupName}`);
      }
    };
  }, [shortcut, groupName, updateCardByName, debug]);
  
  // Check if the group should be visible
  const shouldRender = useGlobalContext 
    ? (globalVisibility && localGroupState.isVisible !== false) 
    : (localGroupState.isVisible !== false);
  
  // Don't render if not visible
  if (!shouldRender) {
    if (debug) {
      console.log(`GroupContainer: Group ${groupName} is hidden`);
    }
    return null;
  }
  
  // Merge style props
  const mergedStyles = {
    ...style
  };

  // Render the children wrapped in a div
  return (
    <div 
      className={className} 
      style={mergedStyles} 
      data-group-name={groupName}
      {...otherProps}
    >
      {children}
    </div>
  );
};

export default GroupContainer;